(() => {
  'use strict';

  const CFG = {
    gridSelectors: [
      'main .collection-listing .product-list',
      'main [data-ajax-container] .product-list',
      'main .product-list',
      'main #product-grid',
      'main .product-grid',
      'main [data-product-grid]'
    ],

    paginationSelectors: [
      'main .pagination-row',
      'main nav.pagination',
      'main .pagination',
      'main [data-pagination]'
    ],

    /*
     * Customer exact bottom तक पहुंचे उससे पहले
     * अगला product page load होना शुरू होगा.
     */
    loadAhead: '1000px 0px',

    loadingText: 'Loading more products…',
    errorText: 'Unable to load more products. Click to retry.'
  };

  const state = {
    grid: null,
    pagination: null,
    sentinel: null,
    observer: null,
    mutationObserver: null,
    nextUrl: null,
    loading: false,
    loadedUrls: new Set(),
    signature: '',
    reinitTimer: null
  };

  function isListingPage() {
    const path = location.pathname.replace(/\/$/, '');

    return (
      path === '/search' ||
      path.includes('/collections/')
    );
  }

  function listingSignature() {
    const url = new URL(location.href);

    url.searchParams.delete('page');
    url.searchParams.delete('phcursor');

    return `${url.pathname}?${url.searchParams.toString()}`;
  }

  function productKey(card) {
    const link = card?.matches?.('a[href*="/products/"]')
      ? card
      : card?.querySelector?.('a[href*="/products/"]');

    if (!link) {
      return null;
    }

    try {
      return new URL(
        link.getAttribute('href'),
        location.origin
      ).pathname.replace(/\/$/, '');
    } catch (error) {
      return null;
    }
  }

  function directProductCards(grid) {
    if (!grid) {
      return [];
    }

    return Array.from(grid.children).filter((child) => {
      return Boolean(productKey(child));
    });
  }

  /*
   * वह product grid select करता है जिसमें
   * सबसे अधिक actual product cards हैं.
   */
  function findBestGrid(root = document) {
    const candidates = [];

    CFG.gridSelectors.forEach((selector) => {
      try {
        root.querySelectorAll(selector).forEach((element) => {
          if (!candidates.includes(element)) {
            candidates.push(element);
          }
        });
      } catch (error) {
        console.warn(
          '[Dezin Infinite Scroll] Invalid grid selector:',
          selector
        );
      }
    });

    return candidates
      .map((grid) => ({
        grid,
        count: directProductCards(grid).length
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count)[0]?.grid || null;
  }

  function findPagination(root = document) {
    for (const selector of CFG.paginationSelectors) {
      try {
        const element = root.querySelector(selector);

        if (element) {
          return element;
        }
      } catch (error) {
        console.warn(
          '[Dezin Infinite Scroll] Invalid pagination selector:',
          selector
        );
      }
    }

    return null;
  }

  function absoluteUrl(href, baseUrl) {
    if (!href) {
      return null;
    }

    try {
      const url = new URL(href, baseUrl);

      /*
       * केवल इसी Shopify store की URL allow होगी.
       */
      if (url.origin !== location.origin) {
        return null;
      }

      return url.href;
    } catch (error) {
      return null;
    }
  }

  /*
   * Shopify का वास्तविक next-page link खोजता है.
   *
   * Last page पर वास्तविक Next link नहीं मिलता,
   * इसलिए function null return करेगा.
   *
   * कोई manual/fake page number generate नहीं किया जाएगा.
   */
  function findNextUrl(
    root = document,
    baseUrl = location.href
  ) {
    /*
     * Shopify कभी-कभी <head> में rel="next" देता है.
     */
    const headNext = root.querySelector?.(
      'link[rel="next"][href]'
    );

    if (headNext) {
      const nextUrl = absoluteUrl(
        headNext.getAttribute('href'),
        baseUrl
      );

      if (nextUrl) {
        return nextUrl;
      }
    }

    const pagination = findPagination(root);

    if (!pagination) {
      return null;
    }

    const links = Array.from(
      pagination.querySelectorAll('a[href]')
    );

    /*
     * Next text/class/rel/aria-label से link खोजें.
     */
    const semanticNext = links.find((link) => {
      const rel = (
        link.getAttribute('rel') || ''
      ).toLowerCase();

      const className = String(
        link.className || ''
      ).toLowerCase();

      const ariaLabel = (
        link.getAttribute('aria-label') || ''
      ).toLowerCase();

      const title = (
        link.getAttribute('title') || ''
      ).toLowerCase();

      const text = (
        link.textContent || ''
      ).trim().toLowerCase();

      return (
        rel.split(/\s+/).includes('next') ||
        className.includes('next') ||
        ariaLabel.includes('next') ||
        title.includes('next') ||
        /^(next|next\s*[»›→]|[»›→])$/i.test(text)
      );
    });

    if (semanticNext) {
      const nextUrl = absoluteUrl(
        semanticNext.getAttribute('href'),
        baseUrl
      );

      if (nextUrl) {
        return nextUrl;
      }
    }

    /*
     * Symmetry theme में Next link detect न होने पर
     * वर्तमान page number के बाद वाला numeric link खोजें.
     */
    const currentElement = pagination.querySelector(
      [
        '[aria-current="page"]',
        '.current',
        '.pagination__item--current'
      ].join(',')
    );

    const currentNumber = Number.parseInt(
      (currentElement?.textContent || '').trim(),
      10
    );

    if (Number.isFinite(currentNumber)) {
      const numericNext = links
        .map((link) => ({
          link,
          number: Number.parseInt(
            (link.textContent || '').trim(),
            10
          )
        }))
        .filter((item) => {
          return (
            Number.isFinite(item.number) &&
            item.number > currentNumber
          );
        })
        .sort((a, b) => {
          return a.number - b.number;
        })[0]?.link;

      if (numericNext) {
        const nextUrl = absoluteUrl(
          numericNext.getAttribute('href'),
          baseUrl
        );

        if (nextUrl) {
          return nextUrl;
        }
      }
    }

    /*
     * कोई वास्तविक Next link नहीं मिला:
     * इसका अर्थ सभी products load हो चुके हैं.
     */
    return null;
  }

  function injectStyles() {
    if (
      document.getElementById(
        'dezin-infinite-scroll-css'
      )
    ) {
      return;
    }

    const style = document.createElement('style');

    style.id = 'dezin-infinite-scroll-css';

    style.textContent = `
      .dezin-pagination-hidden {
        display: none !important;
      }

      #dezin-infinite-scroll {
        width: 100%;
        min-height: 72px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 18px 12px;
        box-sizing: border-box;
        clear: both;
        text-align: center;
        font-size: 14px;
        line-height: 1.4;
      }

      #dezin-infinite-scroll[data-state="idle"] {
        min-height: 8px;
        padding: 0;
      }

      .dezin-scroll-spinner {
        width: 20px;
        height: 20px;
        flex: 0 0 20px;
        box-sizing: border-box;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: dezinSpin 0.7s linear infinite;
      }

      .dezin-scroll-retry {
        appearance: none;
        -webkit-appearance: none;
        border: 1px solid currentColor;
        background: transparent;
        color: inherit;
        padding: 10px 14px;
        cursor: pointer;
        font: inherit;
      }

      .dezin-scroll-retry:hover {
        opacity: 0.75;
      }

      @keyframes dezinSpin {
        to {
          transform: rotate(360deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .dezin-scroll-spinner {
          animation-duration: 1.5s;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function hidePagination(root = document) {
    CFG.paginationSelectors.forEach((selector) => {
      try {
        root.querySelectorAll(selector).forEach((element) => {
          element.classList.add(
            'dezin-pagination-hidden'
          );

          element.setAttribute(
            'aria-hidden',
            'true'
          );
        });
      } catch (error) {
        console.warn(
          '[Dezin Infinite Scroll] Pagination hide error:',
          selector
        );
      }
    });
  }

  function removeSentinels() {
    document
      .querySelectorAll('#dezin-infinite-scroll')
      .forEach((element) => {
        element.remove();
      });

    state.sentinel = null;
  }

  function removeSentinelAtEnd() {
    disconnectObserver();

    if (state.sentinel?.isConnected) {
      state.sentinel.remove();
    }

    state.sentinel = null;
    state.nextUrl = null;
  }

  function createSentinel() {
    removeSentinels();

    const element = document.createElement('div');

    element.id = 'dezin-infinite-scroll';
    element.dataset.state = 'idle';

    element.setAttribute(
      'role',
      'status'
    );

    element.setAttribute(
      'aria-live',
      'polite'
    );

    /*
     * Pagination के बाद loader रखा जाएगा.
     * Pagination न मिलने पर product grid के बाद.
     */
    const anchor =
      state.pagination || state.grid;

    anchor.insertAdjacentElement(
      'afterend',
      element
    );

    state.sentinel = element;
  }

  function setStatus(type, text = '') {
    if (!state.sentinel) {
      return;
    }

    state.sentinel.dataset.state = type;
    state.sentinel.innerHTML = '';

    if (type === 'loading') {
      const spinner = document.createElement('span');

      spinner.className =
        'dezin-scroll-spinner';

      spinner.setAttribute(
        'aria-hidden',
        'true'
      );

      const label = document.createElement('span');

      label.textContent = text;

      state.sentinel.append(
        spinner,
        label
      );

      return;
    }

    if (type === 'error') {
      const button = document.createElement('button');

      button.type = 'button';

      button.className =
        'dezin-scroll-retry';

      button.textContent = text;

      button.addEventListener(
        'click',
        loadNextPage,
        { once: true }
      );

      state.sentinel.appendChild(button);

      return;
    }

    state.sentinel.textContent = text;
  }

  function disconnectObserver() {
    if (!state.observer) {
      return;
    }

    state.observer.disconnect();
    state.observer = null;
  }

  function observeSentinel() {
    disconnectObserver();

    if (!state.sentinel || !state.nextUrl) {
      return;
    }

    /*
     * Modern browser observer.
     */
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(
        (entries) => {
          const reachedBottom = entries.some(
            (entry) => entry.isIntersecting
          );

          if (reachedBottom) {
            loadNextPage();
          }
        },
        {
          root: null,
          rootMargin: CFG.loadAhead,
          threshold: 0
        }
      );

      state.observer.observe(state.sentinel);
      return;
    }

    /*
     * पुराने browser के लिए scroll fallback.
     */
    window.addEventListener(
      'scroll',
      fallbackScrollHandler,
      { passive: true }
    );
  }

  function fallbackScrollHandler() {
    if (
      !state.sentinel ||
      !state.nextUrl
    ) {
      window.removeEventListener(
        'scroll',
        fallbackScrollHandler
      );

      return;
    }

    const rect =
      state.sentinel.getBoundingClientRect();

    if (rect.top <= window.innerHeight + 1000) {
      loadNextPage();
    }
  }

  async function loadNextPage() {
    if (
      state.loading ||
      !state.nextUrl ||
      !state.grid?.isConnected
    ) {
      return;
    }

    if (state.loadedUrls.has(state.nextUrl)) {
      return;
    }

    const requestUrl = state.nextUrl;

    state.loading = true;
    state.loadedUrls.add(requestUrl);

    setStatus(
      'loading',
      CFG.loadingText
    );

    try {
      const response = await fetch(
        requestUrl,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}`
        );
      }

      const html = await response.text();

      const nextDocument =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const nextGrid =
        findBestGrid(nextDocument);

      /*
       * Request सफल है लेकिन next page पर product grid
       * नहीं है तो products समाप्त हो चुके हैं.
       */
      if (!nextGrid) {
        removeSentinelAtEnd();
        return;
      }

      const incomingCards =
        directProductCards(nextGrid);

      /*
       * कोई incoming product नहीं है:
       * quietly stop करें.
       */
      if (!incomingCards.length) {
        removeSentinelAtEnd();
        return;
      }

      /*
       * Existing products के product URLs collect करें,
       * ताकि duplicate cards append न हों.
       */
      const existingKeys = new Set(
        directProductCards(state.grid)
          .map(productKey)
          .filter(Boolean)
      );

      const fragment =
        document.createDocumentFragment();

      let addedProducts = 0;

      incomingCards.forEach((card) => {
        const key = productKey(card);

        if (key && existingKeys.has(key)) {
          return;
        }

        const importedCard =
          document.importNode(
            card,
            true
          );

        fragment.appendChild(importedCard);

        if (key) {
          existingKeys.add(key);
        }

        addedProducts += 1;
      });

      /*
       * Shopify ने same products दोबारा भेजे:
       * endless loop रोकें और loader quietly remove करें.
       */
      if (!addedProducts) {
        removeSentinelAtEnd();
        return;
      }

      state.grid.appendChild(fragment);

      /*
       * Downloaded page से वास्तविक next-page URL लें.
       * Last page पर यह null होगा.
       */
      state.nextUrl = findNextUrl(
        nextDocument,
        requestUrl
      );

      hidePagination();

      /*
       * Symmetry theme को product card/image layout
       * दोबारा calculate करने में मदद करता है.
       */
      window.dispatchEvent(
        new Event('resize')
      );

      document.dispatchEvent(
        new CustomEvent(
          'dezin:products-loaded',
          {
            detail: {
              added: addedProducts,
              url: requestUrl,
              container: state.grid
            }
          }
        )
      );

      /*
       * Last page:
       * कोई error या end message नहीं दिखेगा.
       */
      if (!state.nextUrl) {
        removeSentinelAtEnd();
        return;
      }

      setStatus('idle');
      observeSentinel();
    } catch (error) {
      console.error(
        '[Dezin Infinite Scroll]',
        error
      );

      /*
       * Genuine network/server error होने पर ही
       * retry button दिखेगा.
       */
      state.loadedUrls.delete(requestUrl);

      setStatus(
        'error',
        CFG.errorText
      );
    } finally {
      state.loading = false;
    }
  }

  function init(force = false) {
    if (!isListingPage()) {
      return;
    }

    const grid = findBestGrid(document);

    if (!grid) {
      console.warn(
        '[Dezin Infinite Scroll] Product grid not found.'
      );

      return;
    }

    const signature =
      listingSignature();

    if (
      !force &&
      state.grid === grid &&
      state.signature === signature &&
      (
        state.sentinel?.isConnected ||
        state.nextUrl === null
      )
    ) {
      hidePagination();
      return;
    }

    disconnectObserver();

    window.removeEventListener(
      'scroll',
      fallbackScrollHandler
    );

    state.grid = grid;

    state.pagination =
      findPagination(document);

    /*
     * Pagination hide करने से पहले वास्तविक
     * next-page URL पढ़ना जरूरी है.
     */
    state.nextUrl = findNextUrl(
      document,
      location.href
    );

    state.loading = false;
    state.loadedUrls.clear();
    state.signature = signature;

    hidePagination();

    /*
     * केवल तब loader बनाएं जब अगला page मौजूद हो.
     * एक ही page होने पर नीचे कुछ नहीं दिखेगा.
     */
    if (state.nextUrl) {
      createSentinel();
      setStatus('idle');
      observeSentinel();
    } else {
      removeSentinels();
    }

    console.log(
      '[Dezin Infinite Scroll] Ready',
      {
        products:
          directProductCards(state.grid).length,

        nextUrl:
          state.nextUrl
      }
    );
  }

  /*
   * Filter या sorting के बाद Symmetry theme
   * product grid replace कर सकता है.
   */
  function scheduleReinitialisation() {
    clearTimeout(
      state.reinitTimer
    );

    state.reinitTimer = setTimeout(() => {
      const currentGrid =
        findBestGrid(document);

      const listingChanged =
        currentGrid &&
        (
          currentGrid !== state.grid ||
          listingSignature() !==
            state.signature
        );

      if (listingChanged) {
        init(true);
      } else {
        hidePagination();
      }
    }, 250);
  }

  function watchThemeUpdates() {
    if (state.mutationObserver) {
      return;
    }

    const target =
      document.querySelector('main') ||
      document.body;

    state.mutationObserver =
      new MutationObserver(
        scheduleReinitialisation
      );

    state.mutationObserver.observe(
      target,
      {
        childList: true,
        subtree: true
      }
    );

    window.addEventListener(
      'popstate',
      () => {
        setTimeout(
          () => init(true),
          100
        );
      }
    );

    document.addEventListener(
      'shopify:section:load',
      () => init(true)
    );
  }

  function start() {
    if (!isListingPage()) {
      return;
    }

    injectStyles();
    init();
    watchThemeUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();